import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Download, GraduationCap, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const { add } = useCart();

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = category === "All" ? products : products.filter((p) => p.category === category);

  const addToCart = (p) => { add(p); toast.success(`${p.name} added to cart`); };

  return (
    <div>
      <section className="bg-[#E8F0EC] dark:bg-blue-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <p className="text-xs uppercase tracking-[0.18em] text-blue-800 dark:text-blue-300 font-semibold mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> T5 Care Academy</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-blue-950 dark:text-blue-50 max-w-2xl leading-tight">Courses, eBooks & digital products</h1>
          <p className="mt-4 text-slate-700 dark:text-slate-300 max-w-2xl text-lg">Certified caregiver training and family resources — purchase once, download instantly with secure Stripe checkout.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 flex-wrap mb-8">
          {categories.map((c) => (
            <button key={c} data-testid={`category-${c.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setCategory(c)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${category === c ? "bg-blue-900 text-white" : "bg-white dark:bg-blue-900/40 text-slate-600 dark:text-slate-200 border border-blue-900/10 hover:border-blue-900/30"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06, duration: 0.5 }}
              data-testid={`product-card-${p.id}`} className="flex flex-col bg-white dark:bg-blue-900/30 rounded-2xl overflow-hidden border border-blue-900/5 shadow-sm hover:shadow-xl transition-shadow group">
              <Link to={`/store/${p.id}`} className="block relative aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {p.badge && <span className="absolute top-3 left-3 bg-amber-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full">{p.badge}</span>}
              </Link>
              <div className="flex flex-col flex-1 p-4">
                <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">{p.category}</p>
                <Link to={`/store/${p.id}`}><h3 className="font-medium text-sm mt-1 line-clamp-2 text-blue-950 dark:text-blue-50 hover:text-blue-700">{p.name}</h3></Link>
                <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 flex-1">{p.short}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-display font-bold text-lg text-blue-900 dark:text-blue-300">${p.price.toFixed(2)}</span>
                  <Button size="sm" data-testid={`product-add-to-cart-${p.id}`} onClick={() => addToCart(p)} className="bg-blue-900 hover:bg-blue-950 text-white gap-1.5">
                    <ShoppingCart className="w-4 h-4" /> Add
                  </Button>
                </div>
                {p.type === "course" && (
                  <Link to={`/store/${p.id}`} data-testid={`preview-lesson-${p.id}`} className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 dark:text-teal-300 hover:gap-2 transition-all">
                    <PlayCircle className="w-3.5 h-3.5" /> Preview lesson 1 free
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-blue-950 text-white p-8 lg:p-12">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-2">T5 Academy for Organizations</p>
          <h2 className="font-display text-2xl lg:text-3xl font-bold">Train your whole team.</h2>
          <p className="mt-2 text-blue-100/80 max-w-2xl">Buy training seats in bulk and assign courses to your staff, with completion tracking across your organization.</p>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[["Team", "10 seats"], ["Business", "25 seats"], ["Enterprise", "50+ seats"]].map(([n, s]) => (
              <div key={n} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <p className="font-display text-lg font-bold">{n}</p>
                <p className="text-teal-300 text-sm">{s}</p>
                <p className="text-xs text-blue-200/70 mt-2">Assign courses · track completion · manage staff</p>
              </div>
            ))}
          </div>
          <Link to="/request-demo" data-testid="academy-orgs-cta" className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-md bg-teal-500 hover:bg-teal-600 text-white font-medium">Get Academy for Organizations</Link>
        </div>
      </section>
    </div>
  );
}
